import { Check, X, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { usePendingReviewBlogArticles, useModerateBlogArticle } from '@/lib/admin/use-blog';

/** Une carte d'article en attente — son propre hook de modération (par id). */
function PendingArticleCard({
  article,
}: {
  article: {
    id: string;
    slug: string;
    title: string;
    subtitle: string;
    reading_time: number;
    author: { display_name: string | null } | null;
  };
}) {
  const moderate = useModerateBlogArticle(article.id);
  return (
    <div className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4">
      <div>
        <p className="font-display text-ink-900 text-[16px] font-bold">{article.title}</p>
        {article.subtitle && (
          <p className="text-ink-500 mt-1 font-sans text-sm">{article.subtitle}</p>
        )}
        <p className="text-ink-400 mt-2 text-xs">
          par {article.author?.display_name ?? 'auteur inconnu'} · {article.reading_time} min ·{' '}
          <code className="font-mono">{article.slug}</code>
        </p>
      </div>

      <Link
        to="/tutor/blog/$articleId"
        params={{ articleId: article.id }}
        className="text-primary-600 inline-flex items-center gap-1 font-sans text-[12px] hover:underline"
      >
        Ouvrir dans l'éditeur <ExternalLink className="h-3 w-3" />
      </Link>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          onClick={() => moderate.mutate({ action: 'reject' })}
          disabled={moderate.isPending}
          className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border px-4 text-sm font-semibold disabled:opacity-50"
        >
          <X className="h-4 w-4" /> Refuser
        </button>
        <Button
          onClick={() => moderate.mutate({ action: 'approve' })}
          disabled={moderate.isPending}
          className="min-h-[2.75rem] w-full sm:w-auto"
        >
          <Check className="h-4 w-4" /> Approuver & publier
        </Button>
      </div>
      {moderate.isError && (
        <p className="text-accent-600 font-sans text-[12px]">{moderate.error?.message}</p>
      )}
    </div>
  );
}

export function TutorAdminBlogModerationPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const { data: articles = [], isLoading } = usePendingReviewBlogArticles(admin);

  if (!admin) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="font-display text-ink-900 text-[24px] font-bold">Modération blog</h1>
        <p className="text-ink-500 font-sans text-[14px]">
          {articles.length} article{articles.length > 1 ? 's' : ''} en attente de validation
        </p>
      </header>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : articles.length === 0 ? (
        <p className="text-ink-500 text-sm">Aucun article en attente.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {articles.map((a) => (
            <PendingArticleCard key={a.id} article={a} />
          ))}
        </div>
      )}
    </div>
  );
}
